import React, { useState, forwardRef, useEffect, useRef, useCallback, useMemo } from "react";
import { Button, Form, Row, Col } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { jwtDecode } from 'jwt-decode';

import {
  GruposService,
  VehiculosService,
  EmpresasService,
} from "../../../../services/apiService";

import { sortBy } from "underscore";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { MultiSelect } from 'primereact/multiselect';
import { useFiltrosStore } from "../../../../lib/store/filtros.store";
import { AccionBitacora, generarObservacion, guardarBitacora } from "../../../../utils/bitacora";
import { createRenderCustomHeader } from '../../../../components/datepicker/CustomDatePickerHeader';
import '../../../../styles/datepicker.css'

dayjs.extend(utc);
dayjs.extend(timezone);

export default function Filtros({ onGenerateReport }) {
  const [grupos, setGrupos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState([]); // Cambiar a selectedVehicle como en recorridos
  const [selectedGrupo, setSelectedGrupo] = useState([]);
  const [selectedEmpresa, setSelectedEmpresa] = useState([]);
  const [btnFiltroActivo, setBtnFiltroActivo] = useState(false);

  const vehiculos = useFiltrosStore((state) => state.vehiculos);
  const setVehiculos = useFiltrosStore((state) => state.setVehiculos);
  const vehiculosFiltrado = useFiltrosStore((state) => state.vehiculosFiltrado);
  const setVehiculosFiltrados = useFiltrosStore((state) => state.setVehiculosFiltrados);

  // Fechas
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

  const [startDate, setStartDate] = useState(startOfDay);
  const [endDate, setEndDate] = useState(endOfDay);
  const [dateRangeError, setDateRangeError] = useState("");

  const filtrosGenerarRef = useRef();

  // Validación de rango de fechas
  useEffect(() => {
    if (startDate && endDate) {
      const diffInMs = endDate - startDate;
      const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
      if (diffInDays > 30) {
        setBtnFiltroActivo(false);
        setDateRangeError("El rango de fechas no puede exceder 30 días.");
      } else {
        setDateRangeError("");
        if (selectedVehicle != null && selectedVehicle.length > 0) {
          setBtnFiltroActivo(true);
        }
      }
    }
  }, [startDate, endDate, selectedVehicle]);

  const loadEmpresas = useCallback(async () => {
    try {
      const response = await EmpresasService.getEmpresas();
      if (response && response.success && response.data) {
        const _empresas = sortBy(response.data, 'razonSocial');
        const empresasData = _empresas.map((empresa) => ({
          value: empresa.empresaUid,
          label: empresa.nombreComercial || empresa.razonSocial,
          empresaUid: empresa.empresaUid,
          empresaId: empresa.empresaId,
          razonSocial: empresa.razonSocial,
          nombreComercial: empresa.nombreComercial
        }));
        setEmpresas(empresasData);
      }
    } catch (error) {
      console.error("Error cargando empresas:", error);
    }
  }, []);

  useEffect(() => {
    // Solo cargar empresas al inicio
    loadEmpresas();
  }, [loadEmpresas]);

  // Efecto para manejar cambios en empresa seleccionada
  useEffect(() => {
    if (selectedEmpresa && selectedEmpresa.length > 0) {
      console.log('[EMPRESAS SELECCIONADAS]', selectedEmpresa);

      // Cargar grupos y vehículos para todas las empresas seleccionadas en una sola llamada
      const loadAllData = async () => {
        try {
          // Usar el backend optimizado que soporta múltiples empresas
          console.log('[CARGANDO DATOS PARA EMPRESAS]', selectedEmpresa);

          // Cargar grupos de todas las empresas seleccionadas
          const gruposResponse = await GruposService.getGruposByEmpresa(selectedEmpresa);
          if (gruposResponse && gruposResponse.success && gruposResponse.data) {
            console.log('[GRUPOS DE TODAS LAS EMPRESAS]', gruposResponse.data);

            const _grupos = sortBy(gruposResponse.data, 'nombre');
            const gruposData = _grupos.map((grupo) => ({
              value: grupo.grupoUid,
              label: grupo.nombre,
              uid_grupo: grupo.grupoUid,
              uid_empresa: grupo.empresaUid
            }));
            setGrupos(gruposData);
          }

          // Cargar vehículos de todas las empresas seleccionadas
          const vehiculosResponse = await VehiculosService.getVehiculosByEmpresa(selectedEmpresa);
          if (vehiculosResponse && vehiculosResponse.success && vehiculosResponse.data) {
            console.log('[VEHICULOS DE TODAS LAS EMPRESAS]', vehiculosResponse.data);

            const _vehiculos = sortBy(vehiculosResponse.data, function (i) {
              return (i.alias ?? "").trim().toUpperCase()
            });
            const vehiculosData = _vehiculos.map(vehiculo => ({
              value: vehiculo.vehiculoUid ?? "",
              label: `${vehiculo.noEconomico ?? ""} / ${vehiculo.alias ?? ""}`,
              uid_grupo: vehiculo.grupoUid ?? "",
              uid_empresa: vehiculo.empresaUid ?? "",
              no_economico: vehiculo.noEconomico ?? "",
              alias: vehiculo.alias ?? "",
              tipo_vehiculo: vehiculo.tipoVehiculo ?? "",
              color_vehiculo: vehiculo.colorVehiculo ?? ""
            }));

            setVehiculos(vehiculosData);
            setVehiculosFiltrados(vehiculosData);
          }

        } catch (error) {
          console.error("Error cargando datos de múltiples empresas:", error);
        }
      };

      loadAllData();

      // Limpiar selecciones previas
      setSelectedGrupo([]);
      setSelectedVehicle([]);
    } else {
      // Si no hay empresas seleccionadas, limpiar grupos y vehículos
      setGrupos([]);
      setVehiculos([]);
      setSelectedGrupo([]);
      setSelectedVehicle([]);
    }
  }, [selectedEmpresa, setVehiculos, setVehiculosFiltrados]);

  // Filtrar vehículos por grupo
  useEffect(() => {
    if (selectedGrupo && selectedGrupo.length > 0) {
      const gruposSeleccionados = selectedGrupo.map(g => g.value || g);
      console.log('[GRUPOS SELECCIONADOS]', gruposSeleccionados);

      // Filtrar vehículos por los grupos seleccionados
      const vehiculosFiltradosPorGrupo = vehiculos.filter(vehiculo =>
        gruposSeleccionados.includes(vehiculo.uid_grupo)
      );

      console.log('[VEHICULOS FILTRADOS POR GRUPO]', vehiculosFiltradosPorGrupo);

      // Usar setVehiculosFiltrados directamente con los vehículos filtrados
      setVehiculosFiltrados(vehiculosFiltradosPorGrupo);
    } else {
      // Cuando no hay grupos seleccionados, mostrar todos los vehículos de la empresa
      setVehiculosFiltrados(vehiculos);
    }
  }, [selectedGrupo, vehiculos, setVehiculosFiltrados]);

  // Asegurar que al cargar los vehículos se inicialice el filtrado
  useEffect(() => {
    if (vehiculos.length > 0) {
      setVehiculosFiltrados(vehiculos); // Mostrar todos los vehículos de la empresa
    }
  }, [vehiculos, setVehiculosFiltrados]);

  useEffect(() => {
    if (selectedVehicle != null && selectedVehicle.length > 0) {
      setBtnFiltroActivo(true);
    } else {
      setBtnFiltroActivo(false);
    }
  }, [selectedVehicle]);

  const generarBitacoraReporte = (accion) => {
    if (accion === AccionBitacora.Generar) {
      let fecha_inicio = dayjs(startDate.getTime()).tz(dayjs.tz.guess()).format("DD/MM/YYYY");
      let hora_inicio = dayjs(startDate.getTime()).tz(dayjs.tz.guess()).format("HH:mm");
      let fecha_fin = dayjs(endDate.getTime()).tz(dayjs.tz.guess()).format("DD/MM/YYYY");
      let dataGrupos = (grupos || []).filter(w => (selectedGrupo || []).includes(w.value));
      let dataVehiculos = (vehiculos || []).filter(w => (selectedVehicle || []).includes(w.value));
      let dataEmpresas = (empresas || []).filter(w => (selectedEmpresa || []).includes(w.value));
      filtrosGenerarRef.current = { fecha_inicio, hora_inicio, fecha_fin, dataGrupos, dataVehiculos, dataEmpresas };
    }
    guardarBitacora({
      categoria: 'Reporte Apagados Encendidos',
      observacion: generarObservacion(
        'Apagados Encendidos',
        {
          fecha_inicio: filtrosGenerarRef.current?.fecha_inicio || '',
          hora_inicio: filtrosGenerarRef.current?.hora_inicio || '',
          fecha_fin: filtrosGenerarRef.current?.fecha_fin || '',
          empresas: filtrosGenerarRef.current?.dataEmpresas?.map(w => w.label).join(', ') || '',
          grupos: filtrosGenerarRef.current?.dataGrupos?.map(w => w.label).join(', ') || '',
          vehiculos: filtrosGenerarRef.current?.dataVehiculos?.map(w => w.label).join(', ') || '',
        }
      ),
      tipoAccion: accion
    });
  };

  const handleGenerate = () => {
    const _fechaInicio = dayjs(startDate.getTime()).tz(dayjs.tz.guess()).format("YYYY-MM-DDTHH:mm:ss");
    const _fechaFin = dayjs(endDate.getTime()).format("YYYY-MM-DDTHH:mm:ss");

    const decoded = jwtDecode(localStorage.getItem('token'));

    // 📊 LOG 1: VEHÍCULOS SELECCIONADOS EN EL COMBO
    console.log('🎯 [FILTROS] === ANÁLISIS DE SELECCIÓN DE VEHÍCULOS ===');
    console.log('🎯 [FILTROS] 1. Vehículos seleccionados en combo:', selectedVehicle?.length || 0);
    console.log('🎯 [FILTROS] 1.1 Contenido selectedVehicle:', selectedVehicle);

    // ✅ VALIDAR Y FILTRAR VEHÍCULOS VÁLIDOS
    // El MultiSelect devuelve strings directamente (UIDs), no objetos

    // 📊 LOG 2: ANTES DE LA LIMPIEZA
    console.log('🔧 [FILTROS] 2. ANTES de limpieza - Total elementos:', selectedVehicle?.length || 0);
    console.log('🔧 [FILTROS] 2.1 Elementos que serán procesados:', selectedVehicle);

    // Proceso de limpieza paso a paso
    const step1_filtered = selectedVehicle.filter(uid => uid && uid !== null && uid !== undefined && uid !== "");
    console.log('🔧 [FILTROS] 2.2 DESPUÉS filtro básico (no null/undefined/empty):', step1_filtered.length);
    console.log('🔧 [FILTROS] 2.3 Elementos eliminados en filtro básico:', selectedVehicle.length - step1_filtered.length);
    console.log('🔧 [FILTROS] 2.4 Elementos eliminados fueron:', selectedVehicle.filter(uid => !(uid && uid !== null && uid !== undefined && uid !== "")));

    const vehiculosValidos = step1_filtered.map(uid => typeof uid === 'string' ? uid : uid.value || uid);
    console.log('🔧 [FILTROS] 2.5 DESPUÉS mapeo a strings:', vehiculosValidos.length);
    console.log('🔧 [FILTROS] 2.6 UIDs finales válidos:', vehiculosValidos);

    // 📊 LOG 3: RESULTADO FINAL
    console.log('✅ [FILTROS] 3. RESULTADO FINAL - Vehículos que se enviarán:', vehiculosValidos.length);
    console.log('✅ [FILTROS] 3.1 Lista de UIDs que se enviarán:', vehiculosValidos);
    console.log('✅ [FILTROS] 3.2 DIFERENCIA: Seleccionados vs Enviados =', (selectedVehicle?.length || 0), 'vs', vehiculosValidos.length, '=', (selectedVehicle?.length || 0) - vehiculosValidos.length, 'perdidos');

    if (vehiculosValidos.length === 0) {
      console.error('❌ No hay vehículos válidos seleccionados');
      return;
    }

    // ✅ PROCESAR EMPRESAS SELECCIONADAS
    const empresasValidas = selectedEmpresa
      .filter(uid => uid && uid !== null && uid !== undefined && uid !== "")
      .map(uid => typeof uid === 'string' ? uid : uid.value || uid);

    generarBitacoraReporte(AccionBitacora.Generar);

    const reportParams = {
      fecha_inicio: _fechaInicio,
      fecha_fin: _fechaFin,
      uids_vehiculo: vehiculosValidos,
      empresaUid: decoded.uid_empresa,
      page: 1,
      limit: 10000,
      uid_usuario: decoded.uid_usuario,
      perfil: 1,
      count: true,
      tipo: "Todo",
      recalculo_geocercas: false,
      order: 1
    };

    // Agregar empresas si hay seleccionadas
    if (empresasValidas.length > 0) {
      reportParams.empresas_seleccionadas = empresasValidas;
    }

    // 📊 LOG 4: PARÁMETROS FINALES QUE SE ENVÍAN
    console.log('📡 [FILTROS] 4. PARÁMETROS FINALES enviados a onGenerateReport:');
    console.log('📡 [FILTROS] 4.1 uids_vehiculo length:', reportParams.uids_vehiculo.length);
    console.log('📡 [FILTROS] 4.2 uids_vehiculo contenido:', reportParams.uids_vehiculo);
    console.log('📡 [FILTROS] 4.3 Todos los parámetros:', reportParams);

    onGenerateReport(reportParams);
  };

  useEffect(() => {
    guardarBitacora({
      categoria: 'Reporte Apagados Encendidos',
      observacion: 'Apagados Encendidos',
      tipoAccion: AccionBitacora.EntrarReporte
    });
  }, []);

  const renderIniHeader = useMemo(
    () => (props) => createRenderCustomHeader(startDate, setStartDate)(props),
    [startDate]
  );
  const renderEndHeader = useMemo(
    () => (props) => createRenderCustomHeader(endDate, setEndDate)(props),
    [endDate]
  );

  const ExampleCustomInput = forwardRef(({ value, onClick }, ref) => (
    <Form.Control
      ref={ref}
      onClick={onClick}
      value={value || ""}
      onChange={() => { }}
      readOnly
    />
  ));

  return (
    <div>
      <Row className="mb-3">
        {dateRangeError && (
          <Col xs={12}>
            <div className="alert alert-warning py-2 my-2">
              {dateRangeError}
            </div>
          </Col>
        )}

        {/* Fecha Inicio */}
        <Col md={2} xs={6}>
          <Form.Group>
            <Form.Label>Desde</Form.Label>
            <DatePicker
              selected={startDate}
              onChange={date => {
                const mismoDia = startDate.getDate() === date.getDate() &&
                  startDate.getMonth() === date.getMonth() &&
                  startDate.getFullYear() === date.getFullYear();
                const nuevaFecha = mismoDia ? date :
                  new Date(date.getFullYear(), date.getMonth(), date.getDate(),
                    startDate.getHours(), startDate.getMinutes(), startDate.getSeconds());
                setStartDate(nuevaFecha);
                if (nuevaFecha > endDate) {
                  setEndDate(nuevaFecha);
                }
              }}
              customInput={<ExampleCustomInput />}
              timeIntervals={5}
              dateFormat="dd/MM/yyyy HH:mm:ss"
              /* showTimeSelect */
              renderCustomHeader={renderIniHeader}
              locale="es"
              timeCaption="Hora"
              maxDate={new Date()}
            />
          </Form.Group>
        </Col>

        {/* Fecha Fin */}
        <Col md={2} xs={6}>
          <Form.Group>
            <Form.Label>Hasta</Form.Label>
            <DatePicker
              selected={endDate}
              minDate={startDate}
              onChange={date => {
                const mismoDia = endDate.getDate() === date.getDate() &&
                  endDate.getMonth() === date.getMonth() &&
                  endDate.getFullYear() === date.getFullYear();
                const nuevaFecha = mismoDia ? date :
                  new Date(date.getFullYear(), date.getMonth(), date.getDate(),
                    endDate.getHours(), endDate.getMinutes(), endDate.getSeconds());
                if (date.getDate() === startDate.getDate() && startDate.getHours() > date.getHours()) {
                  setEndDate(new Date(nuevaFecha.getFullYear(), nuevaFecha.getMonth(),
                    nuevaFecha.getDate(), startDate.getHours(),
                    startDate.getMinutes(), startDate.getSeconds()));
                } else {
                  setEndDate(nuevaFecha);
                }
              }}
              customInput={<ExampleCustomInput />}
              timeIntervals={5}
              dateFormat="dd/MM/yyyy HH:mm:ss"
              /* showTimeSelect */
              renderCustomHeader={renderEndHeader}
              locale="es"
              timeCaption="Hora"
              maxDate={new Date()}
            />
          </Form.Group>
        </Col>

        {/* Empresas */}
        <Col md={2} xs={6}>
          <Form.Label>Empresas</Form.Label>
          <MultiSelect
            className="multiselect-ul w-full"
            value={selectedEmpresa}
            onChange={(e) => setSelectedEmpresa(e.value)}
            options={empresas}
            optionLabel="label"
            filter
            placeholder="Seleccione empresas"
            maxSelectedLabels={2}
            selectedItemsLabel={`${(selectedEmpresa || []).length} empresas seleccionadas`}
          />
        </Col>

        {/* Grupos */}
        <Col md={3} xs={6}>
          <Form.Label>Grupos</Form.Label>
          <MultiSelect
            className="multiselect-ul w-full"
            value={selectedGrupo}
            onChange={(e) => setSelectedGrupo(e.value)}
            options={grupos}
            optionLabel="label"
            filter
            placeholder="Seleccione grupos"
            maxSelectedLabels={2}
            selectedItemsLabel={`${(selectedGrupo || []).length} grupos seleccionados`}
            disabled={!selectedEmpresa || selectedEmpresa.length === 0}
          />
        </Col>

        {/* Vehículos */}
        <Col md={3} xs={6}>
          <Form.Label>Vehículos</Form.Label>
          <MultiSelect
            className="multiselect-ul w-full"
            value={selectedVehicle}
            onChange={(e) => {
              console.log('🎯 [MULTISELECT] Vehículos seleccionados en onChange:', e.value?.length || 0);
              console.log('🎯 [MULTISELECT] Contenido seleccionado:', e.value);
              setSelectedVehicle(e.value);
            }}
            options={vehiculosFiltrado}
            optionLabel="label"
            filter
            placeholder="Seleccione vehículos"
            maxSelectedLabels={3}
            selectedItemsLabel={`${(selectedVehicle || []).length} vehículos seleccionados`}
            disabled={!selectedEmpresa || selectedEmpresa.length === 0}
          />
        </Col>
      </Row>

      <Row className="mt-2">
        <Col md={2} xs={4} className="ms-auto">
          <div className="d-flex justify-content-center mt-4">
            <Button
              variant="primary"
              onClick={handleGenerate}
              className="w-100"
              disabled={!btnFiltroActivo}
            >
              Generar
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  );
}
